import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError, fileViewUrl } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { toast } from "sonner";
import { Check, X, ChatTeardropText, FileImage, MagnifyingGlass, Funnel, Warning } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS_OPTIONS = ["pending", "need_correction", "approved", "active", "rejected", "disqualified"];

export default function RegistrationManagement() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (q) params.q = q;
      const { data } = await api.get("/participants", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const submitAction = async () => {
    if (!selected || !action) return;
    try {
      await api.post(`/participants/${selected.id}/action`, {
        action,
        reason,
        override_duplicate: override,
      });
      toast.success(`Marked ${action}`);
      setSelected(null); setAction(null); setReason(""); setOverride(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Registration Management"
        subtitle="Verify, approve and audit incoming participants. Duplicate accounts are flagged automatically."
        breadcrumbs="Registrations"
        testId="registrations-page"
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="reg-search"
            placeholder="Search name, email, phone, telegram, account…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="w-full bg-[#111827] border border-white/10 rounded-md pl-9 pr-4 py-2 text-sm text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-[#111827] border border-white/10 rounded-md">
          <Funnel size={14} className="text-gray-500" />
          <select
            data-testid="reg-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-transparent text-sm text-white outline-none"
          >
            <option value="">All status</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        </div>
        <button onClick={load} data-testid="reg-refresh" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md">Refresh</button>
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3">Account</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Method</th>
                <th className="px-4 py-3">Submitted</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-gray-500">No registrations found</td></tr>
              ) : rows.map((p) => (
                <tr key={p.id} data-testid={`reg-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{p.full_name}</div>
                    <div className="text-xs text-gray-500 font-mono">{p.broker_server} · {p.account_type}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-300">{p.broker_account_number}</td>
                  <td className="px-4 py-3 text-xs">
                    <div className="text-gray-300">{p.email}</div>
                    <div className="text-gray-500">{p.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400 capitalize">{(p.registration_method || "").replace("_", " ")}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{(p.registration_date || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><StatusBadge status={p.status} testId={`reg-status-${p.id}`} /></td>
                  <td className="px-4 py-3">
                    {p.duplicate_flag && (
                      <span className="inline-flex items-center gap-1 text-xs text-orange-300">
                        <Warning size={14} /> Duplicate
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {p.proof_file_url && (
                        <a href={fileViewUrl(p.proof_file_url)} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-white/5 text-gray-400 hover:text-white" title="View proof">
                          <FileImage size={16} />
                        </a>
                      )}
                      <button data-testid={`reg-approve-${p.id}`} onClick={() => { setSelected(p); setAction("approve"); }} className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-400" title="Approve">
                        <Check size={16} />
                      </button>
                      <button data-testid={`reg-need-correction-${p.id}`} onClick={() => { setSelected(p); setAction("need_correction"); }} className="p-1.5 rounded-md hover:bg-orange-500/10 text-orange-400" title="Need correction">
                        <ChatTeardropText size={16} />
                      </button>
                      <button data-testid={`reg-reject-${p.id}`} onClick={() => { setSelected(p); setAction("reject"); }} className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400" title="Reject">
                        <X size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!action} onOpenChange={(o) => !o && (setAction(null), setSelected(null))}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Confirm: {action?.replace("_", " ")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-400">
              {selected?.full_name} · <span className="font-mono">{selected?.broker_account_number}</span>
            </p>
            {(action === "reject" || action === "need_correction") && (
              <textarea
                data-testid="reg-action-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason / notes"
                rows={3}
                className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-3 text-sm text-white outline-none focus:border-blue-500"
              />
            )}
            {action === "approve" && selected?.duplicate_flag && (
              <label className="flex items-center gap-2 text-sm text-orange-300 p-3 rounded-md bg-orange-500/10 border border-orange-500/30">
                <input type="checkbox" data-testid="reg-override-dup" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                <span>This account is flagged as duplicate. Override (Super Admin only)?</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAction(null)} className="px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button data-testid="reg-action-confirm" onClick={submitAction} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">Confirm</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
